import { useCallback, useMemo } from "react";
import Annotation from "admin/types/interfaces/annotation";
import { useSWRConfig } from "swr";
import { useFetcher } from "~/hooks/useFetcher";
import { apiMutate } from "~/utils/apiClient";
import { safelyCamelize } from "~/utils/object";

const { VITE_ADMIN_CONVERSATIONS_API } = import.meta.env;

interface UseAnnotationsReturn {
  annotations: Annotation[];
  error: Error | undefined;
  isLoading: boolean;
  fetchAnnotations: () => Promise<Annotation[] | undefined>;
  createAnnotation: (content: string) => Promise<Annotation | undefined>;
  updateAnnotation: (annotationId: string, content: string) => Promise<Annotation | undefined>;
  deleteAnnotation: (annotationId: string) => Promise<void>;
}

/**
 * Custom hook for managing conversation annotations with full CRUD operations.
 *
 * Uses useFetcher for data fetching (SWR caching, automatic camelization) and
 * apiMutate for mutations with optimistic updates via SWR's mutate.
 *
 * Error handling:
 * - `fetchAnnotations`: Returns data or undefined, errors available via `error` state
 * - `createAnnotation`, `updateAnnotation`, `deleteAnnotation`: Throw on error
 *   (callers should catch to handle UI feedback like toasts)
 *
 * @param conversationId - The ID of the conversation
 * @returns Object containing annotations data, loading state, and CRUD functions
 */
const useAnnotations = (conversationId: string): UseAnnotationsReturn => {
  const baseUrl = useMemo(
    () => (conversationId ? `${VITE_ADMIN_CONVERSATIONS_API}/${conversationId}/annotations` : null),
    [conversationId]
  );

  const { mutate: globalMutate } = useSWRConfig();

  // Fetch annotations using useFetcher (handles caching, loading, error, camelization)
  const {
    data: annotations = [],
    error,
    isLoading,
    mutate: refetch,
  } = useFetcher<Annotation[]>(baseUrl, {
    shouldCamelize: true,
  });

  // Manual refresh
  const fetchAnnotations = useCallback(async () => {
    if (!baseUrl) {
      console.warn("fetchAnnotations called without conversationId");
      return;
    }
    return await refetch();
  }, [baseUrl, refetch]);

  // Create a new annotation
  const createAnnotation = useCallback(
    async (content: string): Promise<Annotation | undefined> => {
      if (!baseUrl) {
        console.warn("createAnnotation called without conversationId");
        return;
      }

      const rawAnnotation = await apiMutate<Annotation>(baseUrl, "POST", { content });
      const newAnnotation = safelyCamelize(rawAnnotation) as Annotation;

      // Update cache with the new annotation
      await globalMutate<Annotation[]>(baseUrl, (current = []) => [...current, newAnnotation], {
        revalidate: false,
      });

      return newAnnotation;
    },
    [baseUrl, globalMutate]
  );

  // Update an existing annotation with optimistic update
  const updateAnnotation = useCallback(
    async (annotationId: string, content: string): Promise<Annotation | undefined> => {
      if (!baseUrl) {
        console.warn("updateAnnotation called without conversationId");
        return;
      }

      // Optimistic update
      await globalMutate<Annotation[]>(
        baseUrl,
        (current = []) =>
          current.map((ann) => (ann.id === annotationId ? { ...ann, content } : ann)),
        { revalidate: false }
      );

      try {
        const rawAnnotation = await apiMutate<Annotation>(`${baseUrl}/${annotationId}`, "PATCH", {
          content,
        });
        const updatedAnnotation = safelyCamelize(rawAnnotation) as Annotation;

        // Update cache with server response
        await globalMutate<Annotation[]>(
          baseUrl,
          (current = []) =>
            current.map((ann) => (ann.id === annotationId ? updatedAnnotation : ann)),
          { revalidate: false }
        );

        return updatedAnnotation;
      } catch (err) {
        // Rollback by revalidating from server
        await refetch();
        throw err;
      }
    },
    [baseUrl, globalMutate, refetch]
  );

  // Delete an annotation with optimistic update
  const deleteAnnotation = useCallback(
    async (annotationId: string): Promise<void> => {
      if (!baseUrl) {
        console.warn("deleteAnnotation called without conversationId");
        return;
      }

      // Optimistic update
      await globalMutate<Annotation[]>(
        baseUrl,
        (current = []) => current.filter((ann) => ann.id !== annotationId),
        { revalidate: false }
      );

      try {
        await apiMutate(`${baseUrl}/${annotationId}`, "DELETE");
      } catch (err) {
        // Rollback by revalidating from server
        await refetch();
        throw err;
      }
    },
    [baseUrl, globalMutate, refetch]
  );

  return {
    annotations,
    error,
    isLoading,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  };
};

export default useAnnotations;
